import {useCallback, useEffect, useMemo, useState} from 'react';

import {ALREADY_INVITED_MESSAGE, DEBOUNCE_MS, MIN_SEARCH_LEN, NOTIFICATION_TIMEOUTS, USER_NOT_FOUND_MESSAGE} from 'src/constants';
import {useApiWithNotifications} from 'src/hooks/api-with-notification/useApiWithNotifications';
import {useDebounce} from 'src/hooks/use-debounce';
import {useUserLookup} from 'src/hooks/use-user-lookup';
import {useAddUserForm} from 'src/modules/user/shared/use-add-user-form';
import {useUserSearch} from 'src/modules/user/shared/use-user-search';
import {inviteUser} from 'src/openapi/admin/apis/UserIdentityApi';

import type {Error as ApiError} from 'src/openapi/admin/models/Error';
import type {UserShortInfo} from 'src/openapi/admin/models/UserShortInfo';

type Params = {
    vendorId?: string,
    closeDialog: () => void,
    onUserAdded?: () => void,
};

export const useAddVendorUserDialog = ({vendorId, closeDialog, onUserAdded}: Params) => {
    const {form, reset, setField, setUser} = useAddUserForm();
    const {executeWithNotifications} = useApiWithNotifications();
    const {searchUsers, loading: searchLoading, results: suggestUsers, error: searchError} = useUserSearch();
    const {
        lookupUserById,
        loading: userLookupLoading,
        error: lookupError,
        clearError: clearLookupError,
    } = useUserLookup();

    const [emailInput, setEmailInput] = useState('');
    const [shown, setShown] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [isMinQueryShown, setIsMinQueryShown] = useState(false);
    const [searchEnabled, setSearchEnabled] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [userNotFound, setUserNotFound] = useState(false);
    const [alreadyInvited, setAlreadyInvited] = useState(false);

    const VENDOR_ADMIN_ROLE_ID = 'role.eva.vendor.admin';

    useEffect(() => {
        if (vendorId) {
            setField('vendorId', vendorId);
        }
    }, [vendorId, setField]);

    const performSearch = useCallback((term: string) => {
        if (term.length < MIN_SEARCH_LEN) {
            setHasSearched(false);
            return;
        }
        searchUsers(term);
        setHasSearched(true);
    }, [searchUsers]);

    const debouncedSearch = useDebounce(performSearch, DEBOUNCE_MS);

    useEffect(() => {
        if (!searchEnabled) {
            setShown(false);
            setIsMinQueryShown(false);
            return;
        }

        const trimmed = emailInput.trim();

        if (!trimmed) {
            setHasSearched(false);
            setUserNotFound(false);
            setShown(false);
            setIsMinQueryShown(false);
            return;
        }

        if (trimmed.length < MIN_SEARCH_LEN) {
            setHasSearched(false);
            setUserNotFound(false);
            setShown(true);
            setIsMinQueryShown(true);
            return;
        }

        setIsMinQueryShown(false);
        setShown(true);
        debouncedSearch(trimmed);
    }, [emailInput, searchEnabled, debouncedSearch]);

    useEffect(() => {
        if (!searchLoading && hasSearched && emailInput.trim()) {
            if (suggestUsers.length === 0) {
                setUserNotFound(true);
                setError(USER_NOT_FOUND_MESSAGE);
            } else {
                setUserNotFound(false);
                if (error === USER_NOT_FOUND_MESSAGE) {
                    setError(null);
                }
            }
        }
    }, [hasSearched, searchLoading, suggestUsers, emailInput, error]);

    const resetVendorDefaults = useCallback(() => {
        if (vendorId) {
            setField('vendorId', vendorId);
        }
        setField('roleId', undefined);
    }, [setField, vendorId]);

    const handleEmailChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const email = event.currentTarget.value;
        setEmailInput(email);
        setField('email', email);
        if (!searchEnabled) {
            setSearchEnabled(true);
        }
        if (form.user) {
            setUser(null);
            resetVendorDefaults();
        }
        setAlreadyInvited(false);
        setHasSearched(false);
        setUserNotFound(false);
        setError(null);
    }, [
        setField,
        searchEnabled,
        setUser,
        resetVendorDefaults,
        form.user,
    ]);

    const handleUserSelect = useCallback(async (user: UserShortInfo) => {
        setEmailInput(user.email);
        setField('email', user.email);
        setShown(false);
        setSearchEnabled(false);

        const fullUser = await lookupUserById(user.userId);

        if (fullUser) {
            setUser(fullUser);
            setField('roleId', VENDOR_ADMIN_ROLE_ID);
            setUserNotFound(false);
            if (vendorId) {
                setField('vendorId', vendorId);
            }


            const isAlreadyInvited = Boolean(fullUser.role);
            setAlreadyInvited(isAlreadyInvited);

            if (isAlreadyInvited) {
                setError(ALREADY_INVITED_MESSAGE);
            } else {
                setError(null);
            }
        } else {
            setUser(null);
            resetVendorDefaults();
            setUserNotFound(true);
            setError(USER_NOT_FOUND_MESSAGE);
        }
    }, [lookupUserById, setUser, setField, VENDOR_ADMIN_ROLE_ID, vendorId, resetVendorDefaults]);

    const clearSuggest = useCallback(() => {
        setEmailInput('');
        setField('email', '');
        setUser(null);
        setError(null);
        resetVendorDefaults();
        setUserNotFound(false);
        setShown(false);
        setSearchEnabled(true);
        setAlreadyInvited(false);
        clearLookupError();
    }, [setField, setUser, resetVendorDefaults, clearLookupError]);

    const handleInviteClick = useCallback(async () => {
        if (!form.user?.userId || !vendorId) {
            return;
        }

        setBusy(true);
        try {
            const response = await executeWithNotifications<ApiError>(
                inviteUser({
                    inviteUserInfo: {
                        userId: form.user.userId,
                        vendorId,
                        roleId: VENDOR_ADMIN_ROLE_ID,
                    },
                }),
                {
                    success: 'User successfully assigned to vendor',
                    error: 'Unable to assign user. Please try again',
                    exception: 'Unable to assign user. Please try again',
                },
                'vendor-assign-user',
                NOTIFICATION_TIMEOUTS,
            );

            if (!response?.success) {
                setBusy(false);
                return;
            }

            closeDialog();
            onUserAdded?.();
            reset();
            setEmailInput('');
            resetVendorDefaults();
            setHasSearched(false);
            setError(null);
            setUserNotFound(false);
            setSearchEnabled(true);
            setShown(false);
            setAlreadyInvited(false);
            clearLookupError();
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            setError(message);
        } finally {
            setBusy(false);
        }
    }, [
        closeDialog,
        reset,
        resetVendorDefaults,
        setEmailInput,
        clearLookupError,
        form.user,
        vendorId,
        executeWithNotifications,
        VENDOR_ADMIN_ROLE_ID,
        onUserAdded,
    ]);

    const handleClose = useCallback(() => {
        closeDialog();
        reset();
        setEmailInput('');
        resetVendorDefaults();
        setHasSearched(false);
        setError(null);
        setUserNotFound(false);
        setShown(false);
        setSearchEnabled(true);
        setAlreadyInvited(false);
        clearLookupError();
    }, [closeDialog, reset, resetVendorDefaults, clearLookupError]);

    const displayError = useMemo(() => {

        if (alreadyInvited) {
            return ALREADY_INVITED_MESSAGE;
        }
        if (userNotFound) {
            return USER_NOT_FOUND_MESSAGE;
        }
        return error || lookupError || searchError;
    }, [alreadyInvited, error, lookupError, searchError, userNotFound]);

    const isInviteDisabled =
        busy
        || searchLoading
        || userLookupLoading
        || userNotFound
        || alreadyInvited
        || !form.user
        || !vendorId;

    return {
        form,
        emailInput,
        shown,
        hasSearched,
        searchLoading,
        suggestUsers,
        userLookupLoading,
        isInviteDisabled,
        displayError,
        isMinQueryShown,
        alreadyInvited,
        busy,
        handleEmailChange,
        handleUserSelect,
        clearSuggest,
        setShown,
        handleInviteClick,
        handleClose,
    };
};
